import React, { useContext, useEffect, useState } from "react";
import {  Grid, Typography } from "@mui/material";
import { LeadContext } from "LeadContextWrapper";
import { useAuthContext } from "AuthContextWrapper"
import { ScreenContentWrapper } from "views/shared/ScreenContentWrapper";
import { Loading } from "views/shared/Loading";
import { getQueryStringParams } from "@theatticusapp/atticus-shared-components";
import { useNavigate } from "react-router-dom";
import { SCREEN_PATHS } from "../../constants";
import { Estate } from "models/Estate";
import { authApi, estateApi, probateApi } from "apis";
import { User } from "models/User";
import { LeadProbateContext } from "LeadProbateContextWrapper";
import { LeadAPI } from "apis/LeadAPI";
import { UserAPI } from "apis/UserAPI";
import { GeographyContext } from "GeographyContextWrapper";

function delay(ms: number) {
  return new Promise( resolve => setTimeout(resolve, ms) );
}

function delayJitter(minMS: number, maxMS:number): Promise<unknown> {
  if(maxMS <= minMS) {
    maxMS = 100
    minMS = 10
  }

  const delayMS = minMS + Math.random() * (maxMS - minMS)
  return delay(delayMS)
}

export const LeadMigrationScreen = (): JSX.Element => {
  const { lead, doneLoadingLead, leadData, deleteLeadCookie } = useContext(LeadContext)
  const { getFieldsAnswered, getProbateDetails } = useContext(LeadProbateContext)
  const { states, countries } = useContext(GeographyContext)
  const params = getQueryStringParams();
  const navigate = useNavigate();
  const [savingNewEstate, setSavingNewEstate] = useState<boolean>(false)
  const [user, setUser] = useState<User | undefined>() // For some reason the AuthContext version of this does not work so I'm just going to use a local version
  const [doneLoadingUser, setDoneLoadingUser] = useState<boolean>(false)
  const [activeEstate, setActiveEstate] = useState<Estate | undefined>() // For some reason the AuthContext version of this does not work so I'm just going to use a local version
  const [fetchedEstates, setFetchedEstates] = useState<Estate[] | undefined>() // For some reason the AuthContext version of this does not work so I'm just going to use a local version
  const [leadMigrated, setLeadMigrated] = useState<boolean>(false)
  const [migratingLead, setMigratingLead] = useState<boolean>(false)
  const [leadMigrationError, setLeadMigrationError] = useState<string|undefined>()

  // Login...
  useEffect(() => {
    authApi.getUser().then(u => {
      setUser(u || undefined)
      setDoneLoadingUser(true)
    }).catch(() => navigate(SCREEN_PATHS.signOut))
  }, [])

  // Get any active estates
  useEffect(() => {
    if(!user) {
      return
    }

    estateApi.fetchEstates()
      .then(estates => {
        if(estates?.length) {
          setActiveEstate(estates[0]);
        }
        setFetchedEstates(estates || [])
      })
  }, [user])

  // Create an active estate if we don't have one yet
  useEffect(() => {
    if(!fetchedEstates) {
      return 
    }
    if(savingNewEstate || activeEstate) {
      return
    }

    setSavingNewEstate(true)
    const newEstate = new Estate()
    newEstate.saveEstate().then(estate => {
      setActiveEstate(estate)
      setSavingNewEstate(false)
  })
  }, [fetchedEstates, activeEstate, setActiveEstate])

  // If we don't have a lead to migrate or the user didn't authenticate successfully 
  //(we probably will already have redirected if the user didn't authenticate)
  useEffect(() => {
    if(!doneLoadingLead) {
      return
    }
    if(!doneLoadingUser) {
      return
    }

    if(!user) {
      navigate(SCREEN_PATHS.signOut)
    } else if(!lead) {
      setLeadMigrated(true)
    }
  }, [doneLoadingUser, doneLoadingLead, lead, user])

  
  // Migrate lead knowledge engine answers to account
  useEffect(() => {
    (async () => {
      if (user && !migratingLead && activeEstate?.id && getFieldsAnswered && getProbateDetails && deleteLeadCookie && lead?.key && leadData) {
        try {
          console.log("beggining migration of lead to user")
          setMigratingLead(true)
          
          // Set the probate type for the estate.  Must be called separately from save() (on second thought I'm not sure this is true)
          const probateDetails = await getProbateDetails()
          if(probateDetails?.probateType?.id) {
            await estateApi.updateEstate(activeEstate.id, {
              probate_type_id: probateDetails.probateType.id
            })
            activeEstate.probateType = probateDetails.probateType
          } else {
            console.warn("no probate type found for lead " + lead.key)
          }
          activeEstate.userId = user.id

          // Migrate country/state from lead data to estate if it doesn't have them yet
          let stateName = ""
          let countryName = ""
          if(!activeEstate.stateId && leadData["state_id"]) {
            activeEstate.stateId = leadData["state_id"]
            const state = states?.find(s => s.id === activeEstate.stateId)
            if(state) {
              stateName = state.fullName
            }
          }
          if(!activeEstate.countryId && leadData["country_id"]) {
            activeEstate.countryId = leadData["country_id"]
            const country = countries?.find(c => c.id === activeEstate.countryId)
            if(country) {
              countryName = country.fullName
            }
          }

          let jurisdiction = ""
          if(stateName && countryName) {
            jurisdiction = stateName + ", " + countryName
          } else if(stateName) {
            jurisdiction = stateName 
          } else if(countryName) {
            jurisdiction = countryName
          }

          // Migrate name of deceased
          if(leadData["name_of_deceased"] && leadData["name_of_deceased"].trim()) {
            const nameParts = leadData["name_of_deceased"].trim().split(" ") 
            activeEstate.deceasedFirstName = nameParts && nameParts.length > 0 ? nameParts[0] : ""
            if(nameParts[1]) {
              activeEstate.deceasedMiddleName = nameParts[1]
            }
            if(nameParts.length > 2) {
              nameParts[0] = ""
              nameParts[1] = ""
              activeEstate.deceasedLastName = nameParts?.join(" ")?.trim().trim() || ""
            }
          }


          // Update DB copy and local copy of the estate
          await activeEstate.saveEstate()
          setActiveEstate(activeEstate)

          
          // Migrate all knowledge field answers from our lead data table to our regular user/estate tables
          // This must happen after a country/state is set for the estate
          if(activeEstate.stateId) {
            const fieldsAnswered = await getFieldsAnswered()
            fieldsAnswered?.map(async fa => {
              await delayJitter(1, 50)
              await probateApi.saveFieldToKnowledgeEngine(fa);
              
            })
          } else {
            console.error("no state id for active estate, not migrating any knowledge from lead to estate")
          }

          // Tell the database which user this lead converted to
          await LeadAPI.linkToCurrentUserId(lead?.key)
          await delayJitter(1, 50)

          // We've completed migration of our lead to a new user, let's stop tracking them as a lead
          deleteLeadCookie()

          let redirectTo = ""
          if(params.redirect_to) {
            redirectTo = decodeURIComponent(params.redirect_to)
          } 

          await UserAPI.completeProbateQuiz(redirectTo, jurisdiction, probateDetails?.probateType?.name || "", probateDetails?.probateType?.numProbateForms || 0)
          await delayJitter(1, 50)
          await UserAPI.completeOnboarding(true)
          await delayJitter(1, 50)

          console.log("done migrating lead data to user/estate")
          setLeadMigrated(true)

          if(params.redirect_to) {
            const to = decodeURIComponent(params.redirect_to)
            window.location.href = to // can't use navigate() here because it's probably a cross-domain navigation
            return
          } 
        } catch(e) {
          setLeadMigrationError((e as any)?.message)
          console.error("couldn't save lead probate answers: " + (e as any)?.message)
        }
        setMigratingLead(false)
      }
    })();
  }, [user, activeEstate, getFieldsAnswered, getProbateDetails, lead?.key, leadData]);

  return (
    <ScreenContentWrapper
      pageSuffix="Probate"
      title="Getting your account set up"
      step={10}
      totalSteps={10}
      showBeta={false}
      style={{padding:"1rem 3rem 1rem 3rem"}}
    ><>

    {!lead || !activeEstate || !leadMigrated && !leadMigrationError ? (
        <Grid container style={{whiteSpace:"nowrap", verticalAlign:"middle", height:"4rem"}}>
          <Grid item xs={2} sm={2} md={2} lg={2} xl={2} style={{height:"4rem"}}>
            <Loading />
          </Grid>
          <Grid item xs={10} sm={10} md={10} lg={10} xl={10}  style={{height:"4rem", paddingTop:"2rem"}}>
            <Typography variant="h2" component={"p"}>
              You will be redirected shortly!
            </Typography>
          </Grid>
        </Grid>
      ) : (
        <>
          {leadMigrationError && <Typography color="error" component="p">
            An error occurred while creating your acccount.  Please email <a href="mailto:support@weareatticus.biz">support@weareatticus.biz</a>
            for assistance!  
          </Typography>}
        </>
      )}
    </>
    </ScreenContentWrapper>
  );
};

export function encodeProbateQueryParams(leadKey: string, probateName:string, numForms:number, countryName:string, stateName:string):string {
  const vals = JSON.stringify({
    probateName,
    leadKey,
    numForms,
    countryName,
    stateName,
  })
  return encodeURIComponent(btoa(vals))
}

export function decodeProbateQueryParams(encodedVals:string):{probateName:string, leadKey:string, numForms:number, countryName:string, stateName:string} {
  return JSON.parse(atob(decodeURIComponent(encodedVals)))
}